import React, { useEffect } from 'react';
import { Button, Modal, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik } from 'formik';
import { object, string, date } from 'yup';
import { isUndefined } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { FormGroupControl } from '@components/Base';
import { mergeInitialValues } from '@utils/formik';
import { ServerError } from '@components/Helper';
import { UserLess } from '@models/User';

import { GenderFormGroupSelect } from './gender-form-group-select';
import { selectFeatureUser } from './selector';
import { useUpdateEffect } from 'usehooks-ts';
import { userSave, UserSavePayload, userFind, UserFindPayload, StateUpdatePayload, stateUpdate } from './action';
import { yupMobileNumber } from '@utils/yup';

const GROUP_PROPS = { className: 'mb-3' };

const FindStepForm = ({ onHide }: Pick<ModalProps, 'onHide'>) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const initialValues = { name: '', dob: '', gender: '' };
  const validationSchema = object({
    name: string().required(t('validation:required')),
    dob: date().required(t('validation:required')),
    gender: string().required(t('validation:required')),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(payload: UserFindPayload) => {
        dispatch(userFind(payload));
      }}
    >
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>Find User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroupControl
            asRow
            name="name"
            label={t('user.name')}
            required
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="dob"
            label={t('user.dob')}
            type="date"
            required
            groupProps={GROUP_PROPS}
          />
          <GenderFormGroupSelect
            asRow
            name="gender"
            required
            groupProps={GROUP_PROPS}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('confirmModal.cancel')}
          </Button>
          <Button type="submit" variant="primary">
            {t('common:next')}
          </Button>
        </Modal.Footer>
      </Form >
    </Formik>
  );
}

const UpdateStepForm = ({ onHide }: Pick<ModalProps, 'onHide'>) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user, serverErrors } = useSelector(selectFeatureUser);
  const initialValues = mergeInitialValues({
    name: '',
    dob: '',
    gender: '',
    mobileNumber: '',
    email: '',
    occupation: '',
    address: '',
  }, user);
  const validationSchema = object({
    name: string().required(t('validation:required')),
    dob: date().required(t('validation:required')),
    gender: string().required(t('validation:required')),
    mobileNumber: yupMobileNumber().optional(),
    email: string().email().optional(),
    occupation: string().optional(),
    address: string().optional(),
  });

  if (!user) return null;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(payload: UserSavePayload) => {
        dispatch(userSave(payload));
      }}
    >
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>Save User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroupControl
            asRow
            name="name"
            label={t('user.name')}
            required
            disabled
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="dob"
            label={t('user.dob')}
            type="date"
            required
            disabled
            groupProps={GROUP_PROPS}
          />
          <GenderFormGroupSelect
            asRow
            name="gender"
            required
            disabled
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="mobileNumber"
            label={t('user.mobileNumber')}
            disabled={!!user.mobileNumber}
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="email"
            label={t('user.email')}
            disabled={!!user.email}
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="occupation"
            label={t('user.occupation')}
            disabled={!!user.occupation}
            groupProps={GROUP_PROPS}
          />
          <FormGroupControl
            asRow
            name="address"
            label={t('user.address')}
            disabled={!!user.address}
            as="textarea"
            rows={2}
            groupProps={GROUP_PROPS}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {t('confirmModal.cancel')}
          </Button>
          <Button type="submit" variant="primary">
            {t('common:next')}
          </Button>
        </Modal.Footer>
        <ServerError errors={serverErrors} />
      </Form >
    </Formik>
  );
}

type Props = ModalProps & {
  onSuccess: (user: UserLess) => void;
};

const UserFindModal = ({ onSuccess, onHide, ...props }: Props) => {
  const dispatch = useDispatch();
  const { user, isSuccess } = useSelector(selectFeatureUser);

  const handleHide = () => {
    const state: StateUpdatePayload = { serverErrors: {}, user: undefined, isSuccess: false };
    dispatch(stateUpdate(state));
    onHide?.();
  }

  useUpdateEffect(() => {
    if (user?.id && isSuccess) {
      onSuccess(user);
    }
  }, [user, isSuccess]);

  return (
    <Modal
      {...props}
      size="lg"
      onHide={handleHide}
    >
      {isUndefined(user) ? <FindStepForm onHide={handleHide} /> : <UpdateStepForm onHide={handleHide} />}
    </Modal>
  )
};

export { UserFindModal };
